//* react
import React from 'react';

//* material-ui
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

//* icons
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';

import FilesListLayout from 'views/files/FilesListLayout';
import { MediaFile, MediaFileType } from 'types/files';
import useLocalization from 'hooks/useLocalization';

export interface SelectFromGalleryDialogProps {
  open: boolean;
  /** Active gallery category; update via onMediaTypeChange when allowMediaTypeSwitch is true */
  mediaType: MediaFileType;
  onClose: () => void;
  onSelectFile: (file: MediaFile) => void;
  /** When true, shows image / video / document toggles (e.g. chat upload). */
  allowMediaTypeSwitch?: boolean;
  onMediaTypeChange?: (type: MediaFileType) => void;
}

const SelectFromGalleryDialog: React.FC<SelectFromGalleryDialogProps> = ({
  open,
  mediaType,
  onClose,
  onSelectFile,
  allowMediaTypeSwitch = false,
  onMediaTypeChange,
}) => {
  const { currentLocale } = useLocalization();
  const uploadLoc = currentLocale.settingsPopup.uploadFiles;
  const title = uploadLoc.selectFromGalleryTitle ?? uploadLoc.title;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            maxHeight: 'min(78vh, 640px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          edge="end"
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 2,
          px: { xs: 1.5, sm: 2 },
          pb: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        {allowMediaTypeSwitch && (
          <ToggleButtonGroup
            color="primary"
            value={mediaType}
            exclusive
            onChange={(_event, next: MediaFileType | null) => {
              if (next != null) onMediaTypeChange?.(next);
            }}
            aria-label={title}
            sx={{ mb: 1.5, alignSelf: 'stretch' }}
          >
            <ToggleButton value="image" aria-label={uploadLoc.image}>
              <ImageOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="video" aria-label={uploadLoc.video}>
              <VideoFileOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="document" aria-label={uploadLoc.document}>
              <DescriptionOutlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <FilesListLayout
          key={mediaType}
          type={mediaType}
          noActions
          compactPicker
          onClickAction={onSelectFile}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SelectFromGalleryDialog;
